import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { ChevronDown } from 'lucide-react';
import { companyApi } from '../../api/companyApi';

const RevenueChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const res = await companyApi.getCompanies({ limit: 100 });
        const companies = res.data.companies || [];

        // Group companies by month
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentYear = new Date().getFullYear();
        const monthlyStats = Array.from({ length: 12 }, (_, i) => ({
          monthNum: i,
          month: months[i],
          companies: 0,
          employees: 0
        }));

        companies.forEach(c => {
          const date = new Date(c.createdAt || c.onboardedAt || Date.now());
          if (date.getFullYear() === currentYear) {
            const m = date.getMonth();
            monthlyStats[m].companies += 1;
            monthlyStats[m].employees += (c.currentEmployeeCount || 0);
          }
        });

        // Compute cumulative growth
        let cumulativeCompanies = 0;
        let cumulativeEmployees = 0;
        const cumulativeData = monthlyStats.map(stat => {
          cumulativeCompanies += stat.companies;
          cumulativeEmployees += stat.employees;
          return {
            month: stat.month,
            companies: cumulativeCompanies,
            employees: cumulativeEmployees
          };
        });

        // Filter to only show months up to the current month
        const currentMonth = new Date().getMonth();
        setChartData(cumulativeData.slice(0, currentMonth + 1));
      } catch (err) {
        console.error('Failed to load chart data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, []);

  return (
    <div className="bg-white rounded-[2rem] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 h-full flex flex-col">
      {/* Header matching the reference image style */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <h3 className="text-xl font-extrabold text-slate-900 font-display tracking-tight">Tenant Growth</h3>
        
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4 text-sm font-semibold text-slate-600">
            <span className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded bg-green-300" />
              Employees
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded bg-green-700" />
              Companies
            </span>
          </div>
          
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            Today
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      <div className="flex-1 w-full relative min-h-0">
        <div className="absolute inset-0 flex items-center justify-center">
          {loading ? (
            <div className="text-sm text-slate-400 font-medium">Loading chart metrics...</div>
          ) : chartData.length === 0 || chartData.every(d => d.companies === 0) ? (
            <div className="text-sm text-slate-400 font-medium">No tenant scale data available for this year yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barGap={6}>
                <CartesianGrid strokeDasharray="4 4" stroke="#f1f5f9" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="#94a3b8" 
                  tickLine={false} 
                  axisLine={false} 
                  fontSize={12} 
                  dy={12} 
                  tick={{ fill: '#64748b', fontWeight: 500 }}
                />
                <YAxis 
                  stroke="#94a3b8" 
                  tickLine={false} 
                  axisLine={false} 
                  fontSize={12} 
                  dx={-12}
                  tick={{ fill: '#64748b', fontWeight: 500 }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(241, 245, 249, 0.4)' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderColor: '#e2e8f0',
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    color: '#0f172a',
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '12px 16px'
                  }}
                />
                <Bar 
                  dataKey="employees" 
                  fill="#bbc59b" 
                  radius={[4, 4, 0, 0]} 
                  barSize={12} 
                />
                <Bar 
                  dataKey="companies" 
                  fill="#4f5631" 
                  radius={[4, 4, 0, 0]} 
                  barSize={12} 
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
